package com.schoolconnect.app.feature.schoolusers

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.Icon
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.distinctUntilChanged
import com.schoolconnect.app.feature.schoolusers.add.AddSchoolUserSchoolOption
import com.schoolconnect.app.feature.common.SchoolLookupViewModel
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.search
import org.jetbrains.compose.resources.painterResource

@Composable
internal fun SchoolUserSchoolPopup(
    selectedSchool: AddSchoolUserSchoolOption?,
    selectedLabel: String,
    query: String,
    expanded: Boolean,
    filteredSchools: List<AddSchoolUserSchoolOption>,
    onQueryChange: (String) -> Unit,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onSchoolSelected: (AddSchoolUserSchoolOption) -> Unit,
    error: String,
    fieldTextColor: Color,
    mutedTextColor: Color,
    primaryColor: Color,
    borderColor: Color,
    dangerColor: Color,
    surfaceColor: Color,
    highlightBorderColor: Color
) {
    var anchorSize by remember { mutableStateOf(IntSize.Zero) }
    val lookupViewModel = remember { SchoolLookupViewModel() }
    val lookupState by lookupViewModel.uiState.collectAsState()
    val listState = rememberLazyListState()
    DisposableEffect(lookupViewModel) { onDispose { lookupViewModel.clear() } }
    LaunchedEffect(expanded, listState) {
        if (expanded) {
            snapshotFlow { listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: -1 }
                .distinctUntilChanged()
                .collectLatest { last ->
                    val current = lookupViewModel.uiState.value
                    if (current.hasMore && current.options.isNotEmpty() && last >= current.options.lastIndex - 1) {
                        lookupViewModel.loadMore()
                    }
                }
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .onGloballyPositioned { anchorSize = it.size }
    ) {
        val popupWidth = with(LocalDensity.current) { anchorSize.width.toDp() }

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(40.dp)
                .border(
                    1.dp,
                    when {
                        expanded -> primaryColor
                        error.isNotBlank() -> dangerColor
                        else -> borderColor
                    },
                    RoundedCornerShape(8.dp)
                )
                .background(surfaceColor, RoundedCornerShape(8.dp))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onToggleExpanded
                )
                .padding(horizontal = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                text = selectedLabel.ifBlank { "Search and select school" },
                color = if (selectedLabel.isBlank()) mutedTextColor else fieldTextColor,
                fontSize = 14.sp,
                fontWeight = FontWeight.Medium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f)
            )
            Icon(
                painter = painterResource(Res.drawable.chevron_down),
                contentDescription = null,
                tint = primaryColor,
                modifier = Modifier
                    .size(20.dp)
                    .rotate(if (expanded) 180f else 0f)
            )
        }

        if (expanded) {
            Popup(
                popupPositionProvider = anchoredPopupPositionProvider(),
                properties = PopupProperties(
                    focusable = true,
                    dismissOnClickOutside = true,
                    dismissOnBackPress = true
                ),
                onDismissRequest = onDismissExpanded
            ) {
                Surface(
                    modifier = Modifier.width(popupWidth),
                    shape = RoundedCornerShape(14.dp),
                    color = surfaceColor,
                    shadowElevation = 16.dp,
                    tonalElevation = 0.dp,
                    border = androidx.compose.foundation.BorderStroke(1.dp, highlightBorderColor)
                ) {
                    Column {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(58.dp)
                                .padding(horizontal = 18.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.search),
                                contentDescription = null,
                                tint = mutedTextColor,
                                modifier = Modifier.size(22.dp)
                            )
                            Spacer(modifier = Modifier.size(10.dp))
                            BasicTextField(
                                value = lookupState.query,
                                onValueChange = { value ->
                                    lookupViewModel.onQueryChanged(value)
                                    onQueryChange(value)
                                },
                                singleLine = true,
                                textStyle = TextStyle(
                                    fontSize = 14.sp,
                                    lineHeight = 14.sp,
                                    fontWeight = FontWeight.Medium,
                                    color = fieldTextColor
                                ),
                                cursorBrush = SolidColor(primaryColor),
                                modifier = Modifier.fillMaxWidth()
                            ) { innerTextField ->
                                Box(
                                    modifier = Modifier.fillMaxWidth(),
                                    contentAlignment = Alignment.CenterStart
                                ) {
                                    if (lookupState.query.isBlank()) {
                                        Text(
                                            text = "Search school...",
                                            color = mutedTextColor,
                                            fontSize = 14.sp,
                                            fontWeight = FontWeight.Medium,
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    }
                                    innerTextField()
                                }
                            }
                        }

                        if (lookupState.isLoading && lookupState.options.isEmpty()) {
                            Box(Modifier.fillMaxWidth().height(100.dp), contentAlignment = Alignment.Center) {
                                CircularProgressIndicator(modifier = Modifier.size(22.dp), strokeWidth = 2.dp, color = primaryColor)
                            }
                        } else if (lookupState.options.isEmpty()) {
                                Text(
                                    text = "No schools found",
                                    color = mutedTextColor,
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.Medium,
                                    modifier = Modifier.padding(horizontal = 18.dp, vertical = 18.dp)
                                )
                        } else {
                            LazyColumn(state = listState, modifier = Modifier.fillMaxWidth().heightIn(max = 260.dp).padding(bottom = 10.dp)) {
                                items(lookupState.options, key = { it.id }) { option ->
                                    val school = AddSchoolUserSchoolOption(id = option.id, name = option.name)
                                    val selected = selectedSchool?.id == school.id
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(44.dp)
                                            .background(
                                                if (selected) highlightBorderColor.copy(alpha = 0.18f) else Color.Transparent
                                            )
                                            .clickable(onClick = {
                                                onSchoolSelected(school)
                                                onDismissExpanded()
                                            })
                                            .padding(horizontal = 18.dp),
                                        contentAlignment = Alignment.CenterStart
                                    ) {
                                        Text(
                                            text = school.name,
                                            color = fieldTextColor,
                                            fontSize = 14.sp,
                                            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    }
                                }
                                if (lookupState.isLoadingMore) item {
                                    Box(Modifier.fillMaxWidth().padding(10.dp), contentAlignment = Alignment.Center) {
                                        CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp, color = primaryColor)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    if (error.isNotBlank()) {
        Text(
            text = error,
            color = dangerColor,
            fontSize = 13.sp,
            fontWeight = FontWeight.Medium
        )
    }
}

private fun anchoredPopupPositionProvider(): PopupPositionProvider {
    return object : PopupPositionProvider {
        override fun calculatePosition(
            anchorBounds: IntRect,
            windowSize: IntSize,
            layoutDirection: androidx.compose.ui.unit.LayoutDirection,
            popupContentSize: IntSize
        ): IntOffset {
            val x = anchorBounds.left.coerceIn(0, (windowSize.width - popupContentSize.width).coerceAtLeast(0))
            val belowY = anchorBounds.bottom + 10
            val aboveY = anchorBounds.top - popupContentSize.height - 10
            val y = if (belowY + popupContentSize.height <= windowSize.height) {
                belowY
            } else {
                aboveY.coerceAtLeast(0)
            }
            return IntOffset(x, y)
        }
    }
}
